import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { FLAVOURS, PACK_IMG, shippingLabel } from "@/data/content";
import { bagsOf, isBuilder, money, perBagMoney, useFlavourStock, useShop } from "@/lib/shop";
import { useCart, boxBags } from "@/lib/cart";
import { BoxScene } from "@/components/site/BoxScene";
import { BoxProgress } from "@/components/site/BoxProgress";
import { FlavourRow } from "@/components/site/FlavourRow";
import { ProductInfo } from "@/components/site/ProductInfo";

const SIZES = [12, 24, 50];
const BY_KEY = Object.fromEntries(FLAVOURS.map((f) => [f.key, f]));

const SizeCard = ({ s, p, active, onClick }) => (
  <button data-testid={`builder-size-${s}`} onClick={onClick}
    className={`rounded-2xl px-3 py-3 text-left transition-colors ${active ? "bg-[#1B1530] text-white" : "bg-white border border-[#1B1530]/10 hover:border-[#1B1530]"}`}>
    <div className="flex items-baseline justify-between gap-2">
      <p className="font-display text-3xl leading-none">{s}</p>
      {s === 12 && <span className="text-[9px] font-bold uppercase tracking-wide text-[#FFB21A]">Popular</span>}
      {s === 50 && <span className="text-[9px] font-bold uppercase tracking-wide text-[#FF5A5F]">Best value</span>}
    </div>
    <p className="mt-1.5 font-bold text-sm">{p ? money(p.price) : "–"}</p>
    <p className={`text-xs ${active ? "text-white/75" : "text-[#4A4560]"}`}>{p ? `${perBagMoney(p.price / s)}/bag` : ""}</p>
  </button>
);

export default function BuildBoxPage() {
  const [params, setParams] = useSearchParams();
  const size = SIZES.includes(Number(params.get("size"))) ? Number(params.get("size")) : 12;
  const [placed, setPlaced] = useState([]);
  const uid = useRef(0);
  const { products } = useShop();
  const { addBox, setOpen, items } = useCart();
  const shopStock = useFlavourStock();
  // bags already packed in boxes in the cart count against stock
  const packed = boxBags(items);
  const stock = Object.fromEntries(FLAVOURS.map((f) => [f.key, Math.max(0, shopStock[f.key] - (packed[f.name] || 0))]));
  const inStock = FLAVOURS.filter((f) => stock[f.key] > 0);
  const bagsLeft = FLAVOURS.reduce((n, f) => n + Math.min(stock[f.key], size), 0);
  const boxes = useMemo(() => Object.fromEntries(products.filter(isBuilder).map((p) => [bagsOf(p.title), p])), [products]);
  const product = boxes[size];
  const total = placed.length;
  const mix = useMemo(() => {
    const m = Object.fromEntries(FLAVOURS.map((f) => [f.key, 0]));
    placed.forEach((p) => { m[p.f.key] += 1; });
    return m;
  }, [placed]);

  const make = (keys) => keys.map((k, i) => ({ id: (uid.current += 1), f: BY_KEY[k], delay: i * 0.035 }));
  // Fill the box from flavours that still have bags, never more of a flavour than Shopify has
  const fill = (order) => {
    const left = Object.fromEntries(FLAVOURS.map((f) => [f.key, stock[f.key]]));
    const keys = [];
    for (let i = 0; keys.length < size && i < size * 60; i += 1) {
      const k = order(i);
      if (left[k] > 0) { keys.push(k); left[k] -= 1; }
      if (!FLAVOURS.some((f) => left[f.key] > 0)) break;
    }
    if (keys.length < size) toast(`Only ${keys.length} bags in stock right now, so the box isn't full yet`);
    setPlaced(make(keys));
  };
  const evenSplit = () => inStock.length && fill((i) => inStock[i % inStock.length].key);
  const surprise = () => inStock.length && fill(() => inStock[Math.floor(Math.random() * inStock.length)].key);
  const hasSoldOut = placed.some((p) => mix[p.f.key] > stock[p.f.key]);

  const change = (key, d) => setPlaced((cur) => {
    if (d > 0) return cur.length < size && cur.filter((p) => p.f.key === key).length < stock[key] ? [...cur, ...make([key])] : cur;
    const idx = cur.map((p) => p.f.key).lastIndexOf(key);
    return idx < 0 ? cur : cur.filter((_, i) => i !== idx);
  });

  const pickSize = (s) => {
    setParams({ size: String(s) }, { replace: true });
    setPlaced((cur) => cur.slice(0, s));
  };

  const addToCart = () => {
    if (hasSoldOut) { toast("Swap out the sold-out flavour first"); return; }
    const chosen = FLAVOURS.filter((f) => mix[f.key]).map((f) => ({ name: f.name, qty: mix[f.key] }));
    addBox(product, chosen, PACK_IMG[product.handle]);
    toast.success(`${size}-bag box added to cart`);
    setOpen(true);
  };

  return (
    <section data-testid="build-box-page" className="pt-32 sm:pt-36 pb-24 scroll-mt-0">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-8">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
          <h1 className="font-display text-3xl sm:text-4xl leading-none">Pack your {size} bags.</h1>
          <p className="text-sm text-[#4A4560]">Mix any of our 5 flavours. Bigger box, lower price per bag.</p>
        </div>
        <div className="grid lg:grid-cols-12 gap-5 lg:gap-8 items-start">
          <div className="lg:col-span-5 min-w-0 sticky top-[116px] lg:top-32 z-20 bg-[#FFF6EA] pb-2 lg:pb-0 space-y-3">
            <BoxScene size={size} placed={placed} onAdd={addToCart} canAdd={!hasSoldOut && !!product?.available} addLabel={product && !product.available ? "Out of stock" : "Add to cart"} />
            <div className="hidden lg:block"><BoxProgress size={size} mix={mix} total={total} /></div>
          </div>
          <div className="lg:col-span-7 min-w-0 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {SIZES.map((s) => <SizeCard key={s} s={s} p={boxes[s]} active={s === size} onClick={() => pickSize(s)} />)}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="eyebrow text-[#4A4560]">Your mix · <span data-testid="builder-count-inline">{total}/{size}</span></p>
              <div className="flex flex-wrap gap-2">
                {[["Even split", evenSplit, "even"], ["Surprise me", surprise, "surprise"], ["Clear", () => setPlaced([]), "clear"]].map(([l, fn, id]) => (
                  <button key={id} data-testid={`builder-preset-${id}`} onClick={fn} className="h-8 px-3.5 rounded-full border-2 border-[#1B1530] text-xs font-bold hover:bg-[#1B1530] hover:text-white transition-colors">{l}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {FLAVOURS.map((f) => <FlavourRow key={f.key} f={f} qty={mix[f.key]} canAdd={total < size && mix[f.key] < stock[f.key]} soldOut={stock[f.key] <= 0} left={stock[f.key]} onChange={(d) => change(f.key, d)} />)}
            </div>
            <div className="lg:hidden"><BoxProgress size={size} mix={mix} total={total} /></div>
            <div className="rounded-[24px] bg-white border border-[#1B1530]/10 p-5">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-sm text-[#4A4560]">{size} bags · {size * 22}g protein</p>
                  <p data-testid="builder-price" className="font-display text-4xl leading-none mt-1">{product ? money(product.price) : "–"}</p>
                </div>
                {product && <div className="text-right text-sm"><p className="font-bold">{perBagMoney(product.price / size)} a bag</p><p className="text-[#4A4560]">{shippingLabel(product.price)}</p></div>}
              </div>
              <button data-testid="builder-add-to-cart" onClick={addToCart} disabled={!product || total !== size || !product.available || hasSoldOut}
                className="mt-4 w-full h-14 rounded-full bg-[#FF5A5F] text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#1B1530] transition-colors disabled:bg-[#1B1530]/15 disabled:text-[#4A4560]">
                <ShoppingBag className="h-4 w-4" /> {product && !product.available ? "Out of stock" : total === size ? `Add ${size}-bag box to cart` : `Add ${size - total} more bag${size - total === 1 ? "" : "s"}`}
              </button>
              {hasSoldOut && <p data-testid="builder-soldout-note" className="mt-3 text-sm text-[#FF5A5F] font-semibold">Your box has more of a flavour than we have in stock. Swap some bags for another flavour.</p>}
              {!hasSoldOut && bagsLeft < size && <p data-testid="builder-low-stock-note" className="mt-3 text-sm text-[#FF5A5F] font-semibold">Only {bagsLeft} bags in stock right now, not enough for a {size}-bag box. Try a smaller box.</p>}
              {product && !product.available && <p className="mt-3 text-sm text-[#FF5A5F] font-semibold">This box size is sold out online right now.</p>}
            </div>
            <ProductInfo flavourKeys={FLAVOURS.map((f) => f.key)} />
          </div>
        </div>
      </div>
    </section>
  );
}
